/* eslint-disable ember/no-computed-properties-in-native-classes */
import { reads } from '@ember/object/computed';
import { service } from '@ember/service';
import { Ability } from 'ember-can';
import type AbilitiesService from 'ember-can/services/abilities';
import { isProductDisabled } from 'garaje/helpers/product-disabled';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import type WorkplaceRouteConfigService from 'garaje/services/workplace-route-config';
import includesAny from 'garaje/utils/includes-any';
import { GLOBAL_ADMIN, LOCATION_ADMIN } from 'garaje/utils/roles';
import { Permission } from 'garaje/utils/ui-permissions';

export default class WorkplaceAbility extends Ability {
  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;
  @service declare featureConfig: FeatureConfigService;
  @service declare workplaceRouteConfig: WorkplaceRouteConfigService;
  @service declare abilities: AbilitiesService;

  @reads('state.vrSubscription') vrSubscription!: StateService['vrSubscription'];
  @reads('currentAdmin.roleNames') roles!: CurrentAdminService['roleNames'];

  /**
   * @returns whether admin can enable/disable the "Capture proof of vaccination" feature
   */
  get canToggleProofOfVaccination(): boolean {
    const { roles } = this;
    const canAccessProtectLegacyFeatures = !!this.state.features?.canAccessProtectLegacyFeatures;
    return (
      (!!this.state.features?.canAccessVaccinationUploadAndApproval || canAccessProtectLegacyFeatures) &&
      includesAny(roles, GLOBAL_ADMIN)
    );
  }

  /**
   * @returns whether admin can enable/disable the "Capture SARS-CoV-2 test results" feature
   */
  get canToggleProofOfCovidNegativeTest(): boolean {
    const { roles } = this;
    const canAccessProtectLegacyFeatures = !!this.state.features?.canAccessProtectLegacyFeatures;
    return (
      (!!this.state.features?.canAccessVaccinationUploadAndApproval || canAccessProtectLegacyFeatures) &&
      includesAny(roles, GLOBAL_ADMIN)
    );
  }

  /**
   * @returns whether admin can view SARS-CoV-2 Document Approval Requirements
   */
  get canViewCovidDocumentRequirements(): boolean {
    const { vrSubscription, roles } = this;
    const canAccessProtectLegacyFeatures = !!this.state.features?.canAccessProtectLegacyFeatures;
    return (
      (!!this.state.features?.canAccessVaccinationUploadAndApproval ||
        (canAccessProtectLegacyFeatures && (!!vrSubscription?.isPremiumPlan || !!vrSubscription?.isEnterprisePlan))) &&
      includesAny(roles, GLOBAL_ADMIN)
    );
  }

  /**
   * @returns Admin can visit Workplace Employee settings
   */
  get canVisitSettings(): boolean {
    const routeConfig = this.workplaceRouteConfig.computeWorkplaceRouteConfig();
    return 'canVisitSettings' in routeConfig && routeConfig.canVisitSettings;
  }

  get canModifyAttendanceCorrectionSetting(): boolean {
    const { roles } = this;

    const featureEnabled = this.featureFlags.isEnabled('rto-attendance-correction');

    return featureEnabled && includesAny(roles, GLOBAL_ADMIN, LOCATION_ADMIN);
  }

  get canToggleEaDelegatedBooking(): boolean {
    const { roles } = this;

    const canUseFeature = this.canUseDbeam;

    return canUseFeature && includesAny(roles, GLOBAL_ADMIN, LOCATION_ADMIN);
  }

  /**
   * Note from WRKX-5275: If/when use of legacy admin roles is completely deprecated and replaced by Authz,
   * the logic for this method should be replaced by that in canViewEmployeeInvite. Currently
   * canVisitEmployeeLog encompasses legacy permission types, so canViewEmployeeInvite has been implemented
   * as a separate method for backwards compatibility.
   * */
  get canVisitEmployeeLog(): boolean {
    const routeConfig = this.workplaceRouteConfig.computeWorkplaceRouteConfig();
    return 'canVisitEmployeeLog' in routeConfig && routeConfig.canVisitEmployeeLog;
  }

  /**
   * @returns whether user has Authz permissions to visit Workplace Employee Log
   */
  get canViewEmployeeInvite(): boolean {
    return this.authz.hasAnyPermissionAtCurrentLocation([
      Permission.WORKPLACE_EMPLOYEE_INVITE_READ,
      Permission.DESKS_RESERVATION_READ,
    ]);
  }

  /**
   * @returns whether admin can visit Workplace Announcements
   */
  get canVisitAnnouncements(): boolean {
    const routeConfig = this.workplaceRouteConfig.computeWorkplaceRouteConfig();
    return 'canVisitAnnouncements' in routeConfig && routeConfig.canVisitAnnouncements;
  }

  /**
   * @returns whether admin can visit Workplace Approvals
   */
  get canVisitApprovals(): boolean {
    const { roles } = this;
    return includesAny(roles, GLOBAL_ADMIN, LOCATION_ADMIN);
  }

  /**
   * @returns whether user can visit Workplace Landing Page
   */
  get canVisitStartTrial(): boolean {
    const routeConfig = this.workplaceRouteConfig.computeWorkplaceRouteConfig();
    return routeConfig.type === 'start-trial';
  }

  /**
   * @returns whether user can visit workplace
   */
  get canVisit(): boolean {
    if (isProductDisabled('workplace')) {
      return false;
    }

    const routeConfig = this.workplaceRouteConfig.computeWorkplaceRouteConfig();
    return routeConfig.type !== 'off';
  }

  get canUseDbeam(): boolean {
    return this.abilities.can('use dbeam');
  }
}
